import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, ShoppingCart, Tag, X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fruitCrush_token';

// Rarity colors
const RARITY_COLORS = {
  common: 'border-gray-400 bg-gray-500/10',
  rare: 'border-blue-400 bg-blue-500/10',
  epic: 'border-purple-400 bg-purple-500/10',
  legendary: 'border-yellow-400 bg-yellow-500/10 animate-pulse'
};

const RARITY_TEXT = {
  common: 'text-gray-600',
  rare: 'text-blue-600',
  epic: 'text-purple-600',
  legendary: 'text-yellow-600'
};

export function GrandExchange({ userCoins, onCoinsChange, userInventory, userEquipment, userCollectedFruits }) {
  const [activeTab, setActiveTab] = useState('browse'); // browse, sell, my-listings
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [tradeableItems, setTradeableItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Sell form state
  const [selectedItem, setSelectedItem] = useState(null);
  const [sellQuantity, setSellQuantity] = useState(1);
  const [sellPrice, setSellPrice] = useState(10);

  const getAuthHeader = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? `Bearer ${token}` : '';
  };

  // Fetch tradeable items
  const fetchTradeableItems = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/items`);
      if (response.ok) {
        const data = await response.json();
        setTradeableItems(data.items);
      }
    } catch (error) {
      console.error('Failed to fetch tradeable items:', error);
    }
  }, []);

  // Fetch listings
  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/exchange/listings?limit=50`;
      if (filterType !== 'all') {
        url += `&item_type=${filterType}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings);
      }
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    }
    setLoading(false);
  }, [filterType, searchQuery]);

  // Fetch my listings
  const fetchMyListings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/my-listings`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        const data = await response.json();
        setMyListings(data.listings);
      }
    } catch (error) {
      console.error('Failed to fetch my listings:', error);
    }
  }, []);

  useEffect(() => {
    fetchTradeableItems();
    fetchListings();
    fetchMyListings();
  }, [fetchTradeableItems, fetchListings, fetchMyListings]);

  // Buy an item
  const handleBuy = async (listing) => {
    if (userCoins < listing.total_price) {
      toast.error(`Not enough coins! Need ${listing.total_price}, have ${userCoins}`);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/exchange/buy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({
          listing_id: listing.id,
          quantity: listing.quantity
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        onCoinsChange(-listing.total_price);
        fetchListings();
      } else {
        toast.error(data.detail || 'Failed to purchase');
      }
    } catch (error) {
      toast.error('Failed to purchase item');
    }
  };

  // Create a listing
  const handleCreateListing = async () => {
    if (!selectedItem) {
      toast.error('Select an item to sell');
      return;
    }
    if (sellPrice < 1) {
      toast.error('Price must be at least 1 coin');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/exchange/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({
          item_id: selectedItem.id,
          item_type: selectedItem.type,
          quantity: sellQuantity,
          price_per_unit: sellPrice
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Item listed successfully!');
        setSelectedItem(null);
        setSellQuantity(1);
        setSellPrice(10);
        fetchMyListings();
        fetchListings();
      } else {
        toast.error(data.detail || 'Failed to create listing');
      }
    } catch (error) {
      toast.error('Failed to create listing');
    }
  };

  // Cancel a listing
  const handleCancelListing = async (listingId) => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/cancel/${listingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': getAuthHeader() }
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Listing cancelled');
        fetchMyListings();
      } else {
        toast.error(data.detail || 'Failed to cancel');
      }
    } catch (error) {
      toast.error('Failed to cancel listing');
    }
  };

  // Get user's available items to sell
  const getAvailableItems = () => {
    const items = [];
    
    // Add consumables from inventory
    if (userInventory) {
      Object.entries(userInventory).forEach(([itemId, qty]) => {
        if (qty > 0) {
          const itemInfo = tradeableItems.find(i => i.id === itemId);
          if (itemInfo) {
            items.push({ ...itemInfo, quantity: qty, source: 'inventory' });
          }
        }
      });
    }
    
    // Add collected fruits
    if (userCollectedFruits) {
      Object.entries(userCollectedFruits).forEach(([itemId, qty]) => {
        if (qty > 0) {
          const itemInfo = tradeableItems.find(i => i.id === itemId);
          if (itemInfo) {
            items.push({ ...itemInfo, quantity: qty, source: 'collected_fruits' });
          }
        }
      });
    }
    
    // Add equipment
    if (userEquipment && userEquipment.length > 0) {
      const equipmentCount = {};
      userEquipment.forEach(itemId => {
        equipmentCount[itemId] = (equipmentCount[itemId] || 0) + 1;
      });
      
      Object.entries(equipmentCount).forEach(([itemId, qty]) => {
        const itemInfo = tradeableItems.find(i => i.id === itemId);
        if (itemInfo) {
          items.push({ ...itemInfo, quantity: qty, source: 'equipment' });
        }
      });
    }
    
    return items;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-lg flex items-center gap-2">
          <span>🏛️</span> Grand Exchange
        </h3>
        <div className="flex items-center gap-1 bg-gradient-to-r from-yellow-500 to-orange-400 rounded-full px-3 py-1">
          <span>🪙</span>
          <span className="font-bold text-yellow-900">{userCoins}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {[
          { id: 'browse', label: 'Browse', icon: '🛒' },
          { id: 'sell', label: 'Sell', icon: '💰' },
          { id: 'my-listings', label: 'My Listings', icon: '📋' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-900'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Browse Tab */}
      {activeTab === 'browse' && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Search & Filter */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-9 px-2 rounded-md border text-sm"
            >
              <option value="all">All</option>
              <option value="consumable">Consumables</option>
              <option value="equipment">Equipment</option>
              <option value="cosmetic">Cosmetics</option>
            </select>
          </div>

          {/* Listings */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No listings found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {listings.map(listing => {
                  const itemInfo = tradeableItems.find(i => i.id === listing.item_id);
                  const rarity = itemInfo?.rarity || 'common';
                  
                  return (
                    <div
                      key={listing.id}
                      className={`p-3 rounded-lg border-2 ${RARITY_COLORS[rarity]}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{listing.item_emoji}</span>
                          <div>
                            <p className="font-semibold text-sm">{listing.item_name}</p>
                            <p className="text-xs text-gray-500">
                              by {listing.seller_name} • x{listing.quantity}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-amber-600">🪙 {listing.total_price}</p>
                          <Button
                            size="sm"
                            onClick={() => handleBuy(listing)}
                            disabled={userCoins < listing.total_price}
                            className="mt-1 h-7 text-xs bg-green-500 hover:bg-green-600"
                          >
                            Buy
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      )}

      {/* Sell Tab */}
      {activeTab === 'sell' && (
        <div className="flex-1 flex flex-col min-h-0">
          <p className="text-sm text-gray-500 mb-3">Select an item from your inventory to sell:</p>
          
          <ScrollArea className="flex-1 mb-3">
            <div className="grid grid-cols-2 gap-2">
              {getAvailableItems().map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    setSelectedItem(item);
                    setSellQuantity(1);
                  }}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    selectedItem?.id === item.id
                      ? 'border-amber-400 bg-amber-50'
                      : `${RARITY_COLORS[item.rarity || 'common']} hover:border-amber-300`
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{item.emoji}</span>
                    <div>
                      <p className="font-semibold text-xs">{item.name}</p>
                      <p className="text-[10px] text-gray-500">x{item.quantity}</p>
                    </div>
                  </div>
                </button>
              ))}
              {getAvailableItems().length === 0 && (
                <div className="col-span-2 text-center py-8 text-gray-500">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No items to sell</p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Sell Form */}
          {selectedItem && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">{selectedItem.emoji}</span>
                <div>
                  <p className="font-bold">{selectedItem.name}</p>
                  <p className="text-xs text-gray-500">Available: {selectedItem.quantity}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="text-xs text-gray-600">Quantity</label>
                  <Input
                    type="number"
                    min={1}
                    max={selectedItem.quantity}
                    value={sellQuantity}
                    onChange={(e) => setSellQuantity(Math.min(parseInt(e.target.value) || 1, selectedItem.quantity))}
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Price (per unit)</label>
                  <Input
                    type="number"
                    min={1}
                    value={sellPrice}
                    onChange={(e) => setSellPrice(parseInt(e.target.value) || 1)}
                    className="h-9"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <p className="text-sm">
                  Total: <span className="font-bold text-amber-600">🪙 {sellQuantity * sellPrice}</span>
                </p>
                <Button onClick={handleCreateListing} className="bg-amber-500 hover:bg-amber-600">
                  <Tag className="w-4 h-4 mr-1" /> List for Sale
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* My Listings Tab */}
      {activeTab === 'my-listings' && (
        <ScrollArea className="flex-1">
          {myListings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No active listings</p>
            </div>
          ) : (
            <div className="space-y-2">
              {myListings.map(listing => (
                <div
                  key={listing.id}
                  className="p-3 rounded-lg border bg-white"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{listing.item_emoji}</span>
                      <div>
                        <p className="font-semibold text-sm">{listing.item_name}</p>
                        <p className="text-xs text-gray-500">x{listing.quantity}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-amber-600">🪙 {listing.total_price}</p>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleCancelListing(listing.id)}
                        className="mt-1 h-7 text-xs"
                      >
                        <X className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      )}
    </div>
  );
}
